import React, { useMemo, useState } from 'react';
import {
  EuiButton, EuiButtonEmpty, EuiCallOut, EuiCheckbox, EuiFieldNumber,
  EuiFieldText, EuiFormRow, EuiModal, EuiModalBody,
  EuiModalFooter, EuiModalHeader, EuiModalHeaderTitle, EuiOverlayMask, EuiSpacer, EuiTextArea, EuiSelect,
} from '@elastic/eui';
import './YouTubeSongImporter.css';

const SETTINGS_KEY = 'ctb.youtubeSermonImportSettings';
const SAVED_SOURCES_KEY = 'ctb.youtubeSavedSources';
const MAX_RESULTS = 100;
const MAX_SCAN_RESULTS = 500;
const defaults = {
  channel: '', publisher: '', speaker: '', category: '講道', language: '國語',
  official: '', tag: '講道', maxResults: 25,
  minDurationMinutes: '', maxDurationMinutes: '', publishedAfter: '', publishedBefore: '',
};

const loadSavedSources = () => {
  try {
    const sources = JSON.parse(localStorage.getItem(SAVED_SOURCES_KEY) || '[]');
    return Array.isArray(sources) ? sources.filter((item) => item?.value).slice(0, 20) : [];
  } catch (_) { return []; }
};

const compactSourceName = (value = '') => {
  const input = value.trim();
  if (!input) return '';
  try {
    const url = new URL(input.startsWith('http') ? input : `https://${input}`);
    return url.pathname.split('/').filter(Boolean).slice(-1)[0] || url.hostname;
  } catch (_) { return input.length > 32 ? `${input.slice(0, 29)}...` : input; }
};

const loadSettings = () => {
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') };
  } catch (_) {
    return defaults;
  }
};

export const getYouTubeVideoId = (value = '') => {
  try {
    const url = new URL(value);
    if (url.hostname === 'youtu.be') return url.pathname.split('/').filter(Boolean)[0] || '';
    if (url.hostname.includes('youtube.com')) {
      if (url.pathname === '/watch') return url.searchParams.get('v') || '';
      const parts = url.pathname.split('/').filter(Boolean);
      if (['shorts', 'embed', 'live'].includes(parts[0])) return parts[1] || '';
    }
  } catch (_) { return ''; }
  return '';
};

const channelFilter = (value) => {
  const input = value.trim();
  if (/^UC[\w-]{20,}$/.test(input)) return { id: input };
  if (input.startsWith('@')) return { forHandle: input };
  try {
    const url = new URL(input.startsWith('http') ? input : `https://${input}`);
    const parts = url.pathname.split('/').filter(Boolean);
    if (parts[0] === 'channel' && parts[1]) return { id: parts[1] };
    if (parts[0]?.startsWith('@')) return { forHandle: parts[0] };
  } catch (_) { /* show the friendly error below */ }
  throw new Error('無法辨識頻道，請使用 youtube.com/@handle 或 /channel/UC... 格式。');
};

const playlistIdFrom = (value) => {
  const input = value.trim().replace(/\\&/g, '&');
  if (/^(?:PL|UU|FL|RD|LL|OLAK5uy)[\w-]{8,}$/.test(input)) return input;
  try {
    const url = new URL(input.startsWith('http') ? input : `https://${input}`);
    if (!url.hostname.includes('youtube.com') && url.hostname !== 'youtu.be') return '';
    return url.searchParams.get('list') || '';
  } catch (_) { return ''; }
};

const scriptureRefs = (text = '') => [...new Set(
  (text.match(/(?:[1-3一二三]?\s*)?[\u3400-\u9fff]{1,8}\s*\d{1,3}\s*[:：]\s*\d{1,3}(?:\s*[-–—至]\s*\d{1,3})?/g) || [])
    .map((value) => value.replace(/\s+/g, ' ').trim())
)].join(', ');

const tagsFrom = (configured, description) => [...new Set([
  ...configured.split(/[,，;；]/).map((value) => value.trim()).filter(Boolean),
  ...(description.match(/#[^\s#]+/g) || []).map((value) => value.slice(1)),
])];

const youtubeGet = async (resource, params, key) => {
  const response = await fetch(`https://www.googleapis.com/youtube/v3/${resource}?${new URLSearchParams({ ...params, key })}`);
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error?.message || `YouTube API 請求失敗 (${response.status})`);
  return data;
};

const durationSeconds = (iso = '') => {
  const match = iso.match(/^P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/);
  if (!match) return 0;
  return (Number(match[1] || 0) * 86400) + (Number(match[2] || 0) * 3600)
    + (Number(match[3] || 0) * 60) + Number(match[4] || 0);
};

const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
    : `${minutes}:${String(remainder).padStart(2, '0')}`;
};

const passesFilters = (video, settings) => {
  const minimum = Number(settings.minDurationMinutes || 0) * 60;
  const maximum = Number(settings.maxDurationMinutes || 0) * 60;
  if (minimum && video.durationSeconds < minimum) return false;
  if (maximum && video.durationSeconds > maximum) return false;
  const publishedDate = String(video.publishedAt || '').slice(0, 10);
  if (settings.publishedAfter && publishedDate < settings.publishedAfter) return false;
  if (settings.publishedBefore && publishedDate > settings.publishedBefore) return false;
  return true;
};

const YouTubeSermonImporter = ({ existingSongs, onClose, onImport }) => {
  const [settings, setSettings] = useState(loadSettings);
  const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY || '';
  const [savedSources, setSavedSources] = useState(loadSavedSources);
  const [selectedSavedSource, setSelectedSavedSource] = useState('');
  const [resolvedSourceName, setResolvedSourceName] = useState('');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState({ type: '', text: '' });
  const existingIds = useMemo(() => new Set(existingSongs.map((item) => getYouTubeVideoId(item.link)).filter(Boolean)), [existingSongs]);
  const selectedCount = videos.filter((item) => item.selected && !item.duplicate).length;
  const availableCount = videos.filter((item) => !item.duplicate).length;
  const setField = (name, value) => setSettings((current) => ({ ...current, [name]: value }));
  const setVideo = (id, change) => setVideos((current) => current.map((item) => item.videoId === id ? { ...item, ...change } : item));

  const persistSavedSources = (sources) => {
    setSavedSources(sources);
    localStorage.setItem(SAVED_SOURCES_KEY, JSON.stringify(sources));
  };

  const saveCurrentSource = () => {
    const value = settings.channel.trim();
    if (!value) return setNotice({ type: 'danger', text: '請先輸入 YouTube 頻道或播放列表。' });
    const existing = savedSources.find((item) => item.value === value);
    const entry = {
      id: existing?.id || `${Date.now()}`,
      name: resolvedSourceName || settings.publisher.trim() || compactSourceName(value) || 'YouTube 來源',
      value,
    };
    const next = [entry, ...savedSources.filter((item) => item.value !== value)].slice(0, 20);
    persistSavedSources(next);
    setSelectedSavedSource(entry.id);
    setNotice({ type: 'success', text: `已將「${entry.name}」保存為常用來源。` });
  };

  const removeSelectedSource = () => {
    if (!selectedSavedSource) return;
    persistSavedSources(savedSources.filter((item) => item.id !== selectedSavedSource));
    setSelectedSavedSource('');
    setNotice({ type: 'success', text: '已移除這個常用來源。' });
  };

  const chooseSavedSource = (id) => {
    setSelectedSavedSource(id);
    const source = savedSources.find((item) => item.id === id);
    if (source) {
      setField('channel', source.value);
      setResolvedSourceName(source.name);
    }
  };

  const saveSettings = () => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    setNotice({ type: 'success', text: 'YouTube 來源與信息預設資料已保存在這個裝置。' });
  };

  const fetchVideos = async () => {
    setLoading(true); setVideos([]); setNotice({ type: '', text: '' });
    try {
      if (!apiKey.trim()) throw new Error('尚未設定 YouTube 讀取服務，請在前端環境變數中設定 REACT_APP_YOUTUBE_API_KEY。');
      let playlistId = playlistIdFrom(settings.channel);
      let sourceName = '';
      let sourceDisplayName = '';
      let sourceLabel = '頻道';
      if (playlistId) {
        sourceLabel = '播放列表';
        const playlistData = await youtubeGet('playlists', { part: 'snippet', id: playlistId }, apiKey.trim());
        const playlist = playlistData.items?.[0];
        if (!playlist) throw new Error('找不到這個 YouTube 播放列表，或播放列表不是公開的。');
        sourceName = playlist.snippet?.channelTitle || playlist.snippet?.title || '';
        sourceDisplayName = playlist.snippet?.title || sourceName;
      } else {
        const channelData = await youtubeGet('channels', { part: 'snippet,contentDetails', ...channelFilter(settings.channel) }, apiKey.trim());
        const channel = channelData.items?.[0];
        if (!channel) throw new Error('找不到這個 YouTube 頻道。');
        playlistId = channel.contentDetails?.relatedPlaylists?.uploads;
        if (!playlistId) throw new Error('這個頻道沒有可讀取的上傳影片清單。');
        sourceName = channel.snippet?.title || '';
        sourceDisplayName = sourceName;
      }

      const limit = Math.max(1, Math.min(Number(settings.maxResults) || 25, MAX_RESULTS));
      const seenVideoIds = new Set();
      let mapped = [];
      let scannedCount = 0;
      let pageToken = '';
      do {
        const page = await youtubeGet('playlistItems', {
          part: 'snippet,contentDetails,status', playlistId,
          maxResults: '50', ...(pageToken ? { pageToken } : {}),
        }, apiKey.trim());
        pageToken = page.nextPageToken || '';
        const items = (page.items || []).filter((item) => {
          const videoId = item.contentDetails?.videoId;
          if (!videoId || ['Deleted video', 'Private video'].includes(item.snippet?.title) || seenVideoIds.has(videoId)) return false;
          seenVideoIds.add(videoId);
          return true;
        });
        scannedCount += items.length;
        const ids = items.map((item) => item.contentDetails.videoId);
        const details = {};
        if (ids.length) {
          const detailPage = await youtubeGet('videos', {
            part: 'statistics,status,snippet,contentDetails', id: ids.join(','),
          }, apiKey.trim());
          (detailPage.items || []).forEach((item) => { details[item.id] = item; });
        }
        const filteredBatch = items.map((item) => {
          const videoId = item.contentDetails.videoId;
          const detail = details[videoId] || {};
          const snippet = detail.snippet || item.snippet || {};
          const description = snippet.description || '';
          const duplicate = existingIds.has(videoId);
          return {
            videoId, selected: !duplicate, duplicate,
            thumbnail: snippet.thumbnails?.medium?.url || snippet.thumbnails?.default?.url || '',
            publishedAt: snippet.publishedAt || item.contentDetails.videoPublishedAt || '',
            durationSeconds: durationSeconds(detail.contentDetails?.duration),
            title: snippet.title || '', link: `https://www.youtube.com/watch?v=${videoId}`,
            publisher: settings.publisher || sourceName, category: settings.category,
            language: settings.language, description, speaker: settings.speaker,
            tag: tagsFrom(settings.tag, description), official: settings.official,
            verselist: scriptureRefs(`${snippet.title || ''}\n${description}`),
            viewct: detail.statistics?.viewCount || '0',
          };
        }).filter((video) => passesFilters(video, settings));
        mapped = mapped.concat(filteredBatch);
      } while (pageToken && mapped.length < limit && scannedCount < MAX_SCAN_RESULTS);
      mapped = mapped.slice(0, limit);
      setField('publisher', settings.publisher || sourceName);
      setResolvedSourceName(sourceDisplayName || sourceName);
      setVideos(mapped);
      const scanLimitNote = scannedCount >= MAX_SCAN_RESULTS && mapped.length < limit ? `（已達最多掃描 ${MAX_SCAN_RESULTS} 部）` : '';
      setNotice({ type: 'success', text: `已從${sourceLabel}檢查 ${scannedCount} 部影片，篩選後顯示 ${mapped.length} 部，其中 ${mapped.filter((item) => item.duplicate).length} 部已存在。${scanLimitNote}` });
    } catch (error) {
      setNotice({ type: 'danger', text: error.message || '讀取 YouTube 頻道或播放列表失敗。' });
    } finally { setLoading(false); }
  };

  const importSelected = async () => {
    const selected = videos.filter((item) => item.selected && !item.duplicate);
    if (!selected.length) return setNotice({ type: 'danger', text: '請至少選擇一部未匯入的影片。' });
    setImporting(true); setNotice({ type: '', text: '' });
    try {
      const ids = await onImport(selected.map(({ videoId, selected: checked, duplicate, thumbnail, publishedAt, durationSeconds: seconds, ...document }) => document));
      const imported = new Set(ids);
      setVideos((current) => current.map((item) => imported.has(item.videoId) ? { ...item, selected: false, duplicate: true } : item));
      setNotice({ type: 'success', text: `成功匯入 ${ids.length} 部影片。` });
    } catch (error) {
      setNotice({ type: 'danger', text: error.message || '批次匯入失敗，請稍後再試。' });
    } finally { setImporting(false); }
  };

  return (
    <EuiOverlayMask>
      <EuiModal className="youtube-song-importer" onClose={onClose} style={{ width: 'min(1120px, 96vw)', maxWidth: 1120 }}>
        <EuiModalHeader className="youtube-import-header">
          <div>
            <EuiModalHeaderTitle>從 YouTube 匯入信息</EuiModalHeaderTitle>
            <p>選擇來源、設定篩選條件，確認資料後再一次匯入。</p>
          </div>
        </EuiModalHeader>
        <EuiModalBody className="youtube-import-body">
          {notice.text && <><EuiCallOut color={notice.type} title={notice.text} /><EuiSpacer size="m" /></>}
          <section className="youtube-import-section">
            <div className="youtube-import-section-title">
              <span className="youtube-import-step">1</span>
              <div><h3>選擇 YouTube 來源</h3><p>詩歌與信息共用常用頻道及播放列表，保存一次即可在兩處選用。</p></div>
            </div>
            {savedSources.length > 0 && <div className="youtube-saved-source-row">
              <EuiFormRow label="常用頻道／播放列表" fullWidth>
                <EuiSelect
                  fullWidth
                  value={selectedSavedSource}
                  options={[
                    { value: '', text: '選擇已保存的來源' },
                    ...savedSources.map((item) => ({ value: item.id, text: item.name })),
                  ]}
                  onChange={(e) => chooseSavedSource(e.target.value)}
                />
              </EuiFormRow>
              <EuiButtonEmpty color="danger" iconType="trash" disabled={!selectedSavedSource} onClick={removeSelectedSource}>移除</EuiButtonEmpty>
            </div>}
            <div className="youtube-import-grid youtube-import-source-grid youtube-import-compact-source-grid">
              <EuiFormRow label="頻道或播放列表網址" helpText="例如 youtube.com/@handle 或包含 list= 的網址" fullWidth>
                <EuiFieldText
                  fullWidth
                  placeholder="貼上 YouTube 頻道或播放列表網址"
                  value={settings.channel}
                  onChange={(e) => { setField('channel', e.target.value); setSelectedSavedSource(''); setResolvedSourceName(''); }}
                />
              </EuiFormRow>
              <EuiFormRow label="篩選後顯示數量" helpText={`1–${MAX_RESULTS} 部；API 每頁讀取 50 部`} fullWidth>
                <EuiFieldNumber fullWidth min={1} max={MAX_RESULTS} value={settings.maxResults} onChange={(e) => setField('maxResults', e.target.value)} />
              </EuiFormRow>
            </div>
            <div className="youtube-source-actions">
              <EuiButtonEmpty iconType="starEmpty" disabled={!settings.channel.trim()} onClick={saveCurrentSource}>保存為常用來源</EuiButtonEmpty>
            </div>
          </section>

          <section className="youtube-import-section">
            <div className="youtube-import-section-title">
              <span className="youtube-import-step">2</span>
              <div><h3>篩選影片</h3><p>系統先讀取 YouTube 資料，再套用以下條件；符合的影片才會進入選擇清單。</p></div>
            </div>
            <div className="youtube-import-grid youtube-import-filter-grid">
              <EuiFormRow label="最短時長（分鐘）" helpText="例如 10，可排除短影片" fullWidth>
                <EuiFieldNumber fullWidth min={0} value={settings.minDurationMinutes} onChange={(e) => setField('minDurationMinutes', e.target.value)} />
              </EuiFormRow>
              <EuiFormRow label="最長時長（分鐘）" helpText="留空表示不限" fullWidth>
                <EuiFieldNumber fullWidth min={0} value={settings.maxDurationMinutes} onChange={(e) => setField('maxDurationMinutes', e.target.value)} />
              </EuiFormRow>
              <EuiFormRow label="發佈日期從" fullWidth>
                <EuiFieldText fullWidth type="date" value={settings.publishedAfter} onChange={(e) => setField('publishedAfter', e.target.value)} />
              </EuiFormRow>
              <EuiFormRow label="發佈日期到" fullWidth>
                <EuiFieldText fullWidth type="date" value={settings.publishedBefore} onChange={(e) => setField('publishedBefore', e.target.value)} />
              </EuiFormRow>
            </div>
          </section>

          <section className="youtube-import-section">
            <div className="youtube-import-section-title">
              <span className="youtube-import-step">3</span>
              <div><h3>設定匯入預設值</h3><p>YouTube 不一定提供講員、分類與語言，讀取後可逐篇修改。</p></div>
            </div>
            <div className="youtube-import-grid youtube-import-sermon-default-grid">
              {[
                ['publisher', '發布者'], ['speaker', '講員'], ['category', '分類'],
                ['language', '語言'], ['official', '官方標記'], ['tag', '預設標籤（逗號分隔）'],
              ].map(([name, label]) => (
                <EuiFormRow key={name} label={label} fullWidth>
                  <EuiFieldText fullWidth value={settings[name]} onChange={(e) => setField(name, e.target.value)} />
                </EuiFormRow>
              ))}
            </div>
          </section>

          {videos.length > 0 && <>
            <section className="youtube-import-results">
              <div className="youtube-import-results-heading">
                <div><h3>確認要匯入的信息</h3><p>可修改標題、講員、經文及簡介。</p></div>
                <EuiCheckbox
                  id="youtube-import-select-all"
                  label={`全選未匯入影片（${selectedCount}/${availableCount}）`}
                  checked={availableCount > 0 && selectedCount === availableCount}
                  onChange={(e) => setVideos((current) => current.map((item) => ({ ...item, selected: item.duplicate ? false : e.target.checked })))}
                />
              </div>
              <div className="youtube-import-video-list">
              {videos.map((video) => <div key={video.videoId} className={`youtube-import-video-card${video.duplicate ? ' is-duplicate' : ''}`}>
                <div className="youtube-import-video-layout">
                  <EuiCheckbox id={`youtube-${video.videoId}`} checked={video.selected} disabled={video.duplicate} onChange={(e) => setVideo(video.videoId, { selected: e.target.checked })} />
                  {video.thumbnail && <img className="youtube-import-thumbnail" src={video.thumbnail} alt="" />}
                  <div className="youtube-import-video-fields">
                    <div className="youtube-import-video-field-grid youtube-import-sermon-video-grid">
                      <EuiFormRow label={video.duplicate ? '標題（已匯入）' : '標題'} fullWidth>
                        <EuiFieldText fullWidth value={video.title} disabled={video.duplicate} onChange={(e) => setVideo(video.videoId, { title: e.target.value })} />
                      </EuiFormRow>
                      <EuiFormRow label="講員" fullWidth>
                        <EuiFieldText fullWidth value={video.speaker} disabled={video.duplicate} onChange={(e) => setVideo(video.videoId, { speaker: e.target.value })} />
                      </EuiFormRow>
                      <EuiFormRow label="相關經文" fullWidth>
                        <EuiFieldText fullWidth value={video.verselist} disabled={video.duplicate} onChange={(e) => setVideo(video.videoId, { verselist: e.target.value })} />
                      </EuiFormRow>
                    </div>
                    <EuiFormRow label="簡介" fullWidth>
                      <EuiTextArea fullWidth rows={2} value={video.description} disabled={video.duplicate} onChange={(e) => setVideo(video.videoId, { description: e.target.value })} />
                    </EuiFormRow>
                    <div className="youtube-import-video-meta">
                      {video.publishedAt ? new Date(video.publishedAt).toLocaleDateString() : ''} · {formatDuration(video.durationSeconds)} · {video.publisher} · {Number(video.viewct || 0).toLocaleString()} 次觀看 · <a href={video.link} target="_blank" rel="noreferrer">查看影片</a>
                    </div>
                  </div>
                </div>
              </div>)}
              </div>
            </section>
          </>}
        </EuiModalBody>
        <EuiModalFooter className="youtube-import-footer">
          <div className="youtube-import-footer-secondary">
            <EuiButtonEmpty onClick={onClose}>關閉</EuiButtonEmpty>
            <EuiButtonEmpty iconType="save" onClick={saveSettings}>保存目前設定</EuiButtonEmpty>
          </div>
          <div className="youtube-import-footer-primary">
            <EuiButton iconType="refresh" isLoading={loading} disabled={importing} onClick={fetchVideos}>{videos.length ? '重新讀取' : '讀取影片'}</EuiButton>
            <EuiButton fill isLoading={importing} disabled={!selectedCount || loading} onClick={importSelected}>匯入已選擇的 {selectedCount} 部影片</EuiButton>
          </div>
        </EuiModalFooter>
      </EuiModal>
    </EuiOverlayMask>
  );
};

export default YouTubeSermonImporter;
